import React, { useRef, useState } from 'react';
import {
  Box,
  Card,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface Review {
  author?: string;
  content?: string;
  id?: string;
  is_verified?: string;
  product_attributes?: string;
  rating?: string;
  review_from?: string;
  timestamp?: string;
  title?: string;
  helpful_count?: string;
  images?: string[];
}

interface ProductReviewsProps {
  reviews?: Review[];
}

const CONTENT_LIMIT = 100;

const StarRow: React.FC<{ value: number; size?: number }> = ({ value, size = 16 }) => (
  <Box sx={{ display: 'flex', gap: '1px' }}>
    {[1, 2, 3, 4, 5].map((s) => (
      <Box
        key={s}
        component="span"
        sx={{ fontSize: size, lineHeight: 1, color: value >= s ? '#f5a623' : '#d9d9d9' }}
      >
        ★
      </Box>
    ))}
  </Box>
);

const VerifiedBadge: React.FC = () => (
  <Box
    component="span"
    sx={{
      width: 16,
      height: 16,
      borderRadius: '50%',
      bgcolor: '#f5a623',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontSize: 10,
      fontWeight: 700,
      flexShrink: 0,
    }}
  >
    ✓
  </Box>
);

// ── Review detail modal ────────────────────────────────────────────────────────

interface ReviewModalProps {
  review: Review | null;
  onClose: () => void;
}

const ReviewModal: React.FC<ReviewModalProps> = ({ review, onClose }) => {
  const { t } = useTranslation();
  if (!review) return null;

  const ratingVal = parseFloat(review.rating || '0') || 0;
  const isVerified = review.is_verified === 'true' || review.is_verified === '1';
  const attributes = review.product_attributes
    ? review.product_attributes.split(/[|,]/).map((s) => s.trim()).filter(Boolean)
    : [];
  const images = review.images || [];

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        {/* Close button */}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', top: 12, right: 12 }}
        >
          <Close fontSize="small" />
        </IconButton>

        {/* Author + verified + stars */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {review.author || 'Anonymous'}
            </Typography>
            {isVerified && <VerifiedBadge />}
          </Box>
          <StarRow value={Math.round(ratingVal)} size={16} />
        </Box>

        {/* Date + review_from */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          {[review.timestamp, review.review_from].filter(Boolean).join(' · ')}
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        {/* Images */}
        {images.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            {images.map((src, i) => (
              <Box
                key={i}
                component="img"
                src={src}
                alt={`review-img-${i}`}
                sx={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 1 }}
              />
            ))}
          </Box>
        )}

        {/* Title */}
        {review.title && (
          <Typography variant="body1" sx={{ fontWeight: 700, mb: 1 }}>
            {review.title}
          </Typography>
        )}

        {/* Full content */}
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, whiteSpace: 'pre-line' }}>
          {review.content}
        </Typography>

        {/* Helpful count */}
        {review.helpful_count && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {review.helpful_count} {t('products.peopleFoundHelpful', 'people found this helpful')}
          </Typography>
        )}

        {/* Attribute chips */}
        {attributes.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            {attributes.map((attr, i) => (
              <Chip key={i} label={attr} size="small" variant="outlined" sx={{ fontSize: 11, borderRadius: 1 }} />
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

const ProductReviews: React.FC<ProductReviewsProps> = ({ reviews }) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeReview, setActiveReview] = useState<Review | null>(null);

  if (!reviews || reviews.length === 0) return null;

  const avgRating =
    reviews.reduce((sum, r) => sum + (parseFloat(r.rating || '0') || 0), 0) / reviews.length;

  const scroll = (dir: 'left' | 'right') =>
    scrollRef.current?.scrollBy({ left: dir === 'right' ? 290 : -290, behavior: 'smooth' });

  return (
    <Box sx={{ mb: 4 }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            {t('products.customerReviews')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarRow value={Math.round(avgRating)} size={18} />
            <Typography variant="body2" color="text.secondary">
              {avgRating.toFixed(1)} {t('products.outOf5', 'out of 5')} ({reviews.length}{' '}
              {t('products.reviewsCount', 'reviews')})
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => scroll('left')}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => scroll('right')}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* ── Carousel ── */}
      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          pb: 1,
        }}
      >
        {reviews.map((review, index) => {
          const id = review.id || String(index);
          const ratingVal = parseFloat(review.rating || '0') || 0;
          const isVerified = review.is_verified === 'true' || review.is_verified === '1';
          const content = review.content || '';
          const isTruncated = content.length > CONTENT_LIMIT;
          const displayContent = isTruncated ? content.slice(0, CONTENT_LIMIT) + '...' : content;
          const images = review.images || [];
          const attributes = review.product_attributes
            ? review.product_attributes.split(/[|,]/).map((s) => s.trim()).filter(Boolean)
            : [];

          return (
            <Card
              key={id}
              sx={{
                minWidth: 260,
                maxWidth: 280,
                flex: '0 0 auto',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              {/* Author + verified + stars */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {review.author || 'Anonymous'}
                  </Typography>
                  {isVerified && <VerifiedBadge />}
                </Box>
                <StarRow value={Math.round(ratingVal)} size={14} />
              </Box>

              {/* Date */}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {review.timestamp}
              </Typography>

              {/* Image strip */}
              {images.length > 0 && (
                <Box sx={{ position: 'relative', mt: 0.5 }}>
                  <Box
                    component="img"
                    src={images[0]}
                    alt="review"
                    sx={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 1 }}
                  />
                  {images.length > 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 6,
                        right: 6,
                        bgcolor: 'rgba(0,0,0,0.55)',
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 700,
                        borderRadius: 1,
                        px: 0.75,
                        py: 0.25,
                      }}
                    >
                      +{images.length - 1}
                    </Box>
                  )}
                </Box>
              )}

              {/* Review title */}
              {review.title && (
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {review.title}
                </Typography>
              )}

              {/* Truncated content + Read more */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.5, display: 'inline' }}
                >
                  {displayContent}
                </Typography>
                {isTruncated && (
                  <Typography
                    component="span"
                    variant="body2"
                    onClick={() => setActiveReview(review)}
                    sx={{ color: '#f5a623', cursor: 'pointer', fontWeight: 600, ml: 0.5 }}
                  >
                    {t('products.readMore')}
                  </Typography>
                )}
              </Box>

              {/* Attribute chips */}
              {attributes.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                    pt: 1,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {attributes.map((attr, i) => (
                    <Chip
                      key={i}
                      label={attr}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 10, height: 22, borderRadius: 1 }}
                    />
                  ))}
                </Box>
              )}
            </Card>
          );
        })}
      </Box>

      {/* ── Review detail modal ── */}
      <ReviewModal review={activeReview} onClose={() => setActiveReview(null)} />
    </Box>
  );
};

export default ProductReviews;
